import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import {
  Modal,
  Portal,
  Text,
  TextInput,
  Button,
  Surface,
  IconButton,
  HelperText,
  Divider,
  SegmentedButtons,
  Switch,
} from 'react-native-paper';
import { theme, spacing } from '../constants/theme';

interface QuestionField {
  id?: string;
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'decimal' | 'plusminus' | 'email' | 'tel' | 'date' | 'select' | 'radio' | 'file' | 'helpful_links' | 'dimensions_2d' | 'dimensions_3d';
  required?: boolean;
  placeholder?: string;
  options?: string[];
  triggersMultiplicity?: boolean;
  links?: Array<{ url: string; label: string }>;
  unit?: 'in' | 'ft' | 'cm' | 'm';
}

interface QuestionEditorModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSave: (field: QuestionField) => void;
  existingField?: QuestionField;
  loading?: boolean;
}

export const QuestionEditorModal: React.FC<QuestionEditorModalProps> = ({
  visible,
  onDismiss,
  onSave,
  existingField,
  loading = false,
}) => {
  const [fieldLabel, setFieldLabel] = useState('');
  const [fieldType, setFieldType] = useState<QuestionField['type']>('text');
  const [selectOptions, setSelectOptions] = useState<string[]>(['']);
  const [triggersMultiplicity, setTriggersMultiplicity] = useState(false);
  const [links, setLinks] = useState<Array<{ url: string; label: string }>>([{ url: '', label: '' }]);
  const [unit, setUnit] = useState<'in' | 'ft' | 'cm' | 'm'>('in');
  const [errors, setErrors] = useState<{ label?: string; options?: string }>({});

  // Generate field name from label
  const generateFieldName = (label: string): string => {
    return label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .replace(/^[0-9]/, 'field_$&') // Ensure it starts with a letter
      || 'field';
  };

  // Reset or populate form
  useEffect(() => {
    if (visible) {
      if (existingField) {
        setFieldLabel(existingField.label);
        setFieldType(existingField.type);
        setSelectOptions(existingField.options || ['']);
        setTriggersMultiplicity(existingField.triggersMultiplicity === true);
        setLinks(existingField.links || [{ url: '', label: '' }]);
        setUnit(existingField.unit || 'in');
      } else {
        setFieldLabel('');
        setFieldType('text');
        setSelectOptions(['']);
        setTriggersMultiplicity(false);
        setLinks([{ url: '', label: '' }]);
        setUnit('in');
      }
      setErrors({});
    }
  }, [visible, existingField]);

  const handleSave = () => {
    // Validate
    const newErrors: { label?: string; options?: string } = {};

    if (!fieldLabel.trim()) {
      newErrors.label = 'Question label is required';
    }

    if (fieldType === 'select' || fieldType === 'radio') {
      const validOptions = selectOptions.filter(opt => opt.trim());
      if (validOptions.length < 2) {
        newErrors.options = `${fieldType === 'select' ? 'Select' : 'Radio'} fields must have at least 2 options`;
      }
    }

    if (fieldType === 'helpful_links') {
      const validLinks = links.filter(link => link.url.trim() && link.label.trim());
      if (validLinks.length === 0) {
        newErrors.options = 'At least one link with both URL and label is required';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Generate field name from label (unless editing existing field)
    const fieldName = existingField?.name || generateFieldName(fieldLabel);

    // Save
    const field: QuestionField = {
      name: fieldName,
      label: fieldLabel.trim(),
      type: fieldType,
      required: fieldType === 'helpful_links' ? false : true, // helpful_links not required (no user response)
    };

    if (fieldType === 'select' || fieldType === 'radio') {
      field.options = selectOptions.filter(opt => opt.trim());
    }

    if (fieldType === 'number' || fieldType === 'plusminus') {
      field.triggersMultiplicity = triggersMultiplicity;
    }

    if (fieldType === 'helpful_links') {
      field.links = links.filter(link => link.url.trim() && link.label.trim());
    }

    if (fieldType === 'dimensions_2d' || fieldType === 'dimensions_3d') {
      field.unit = unit;
    }

    onSave(field);
  };

  const addSelectOption = () => {
    setSelectOptions([...selectOptions, '']);
  };

  const updateSelectOption = (index: number, value: string) => {
    const newOptions = [...selectOptions];
    newOptions[index] = value;
    setSelectOptions(newOptions);
  };

  const removeSelectOption = (index: number) => {
    if (selectOptions.length > 1) {
      const newOptions = selectOptions.filter((_, i) => i !== index);
      setSelectOptions(newOptions);
    }
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={styles.modalContent}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={styles.keyboardAvoid}
        >
          <Surface style={styles.surface}>
            <View style={styles.header}>
              <Text variant="headlineSmall" style={styles.title}>
                {existingField ? 'Edit Question' : 'Add Question'}
              </Text>
              <IconButton
                icon="close"
                size={24}
                onPress={onDismiss}
                style={styles.closeButton}
              />
            </View>

            <Divider />

            <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
              <View style={styles.form}>
                <TextInput
                  label="Question Label *"
                  value={fieldLabel}
                  onChangeText={(text) => {
                    setFieldLabel(text);
                    if (errors.label) {
                      setErrors({ ...errors, label: undefined });
                    }
                  }}
                  mode="outlined"
                  error={!!errors.label}
                  disabled={loading}
                  style={styles.input}
                  placeholder="e.g., Model Number"
                />
                <HelperText type="error" visible={!!errors.label}>
                  {errors.label}
                </HelperText>

                <View style={styles.typeSection}>
                  <Text variant="labelLarge" style={styles.typeLabel}>
                    Field Type
                  </Text>
                  <SegmentedButtons
                    value={fieldType}
                    onValueChange={(value) => setFieldType(value as QuestionField['type'])}
                    buttons={[
                      { value: 'text', label: 'Text' },
                      { value: 'textarea', label: 'Long Text' },
                      { value: 'number', label: 'Number' },
                      { value: 'decimal', label: 'Decimal' },
                      { value: 'plusminus', label: '+/- Number' },
                    ]}
                    style={styles.segmentedButtons}
                  />
                  <SegmentedButtons
                    value={fieldType}
                    onValueChange={(value) => setFieldType(value as QuestionField['type'])}
                    buttons={[
                      { value: 'email', label: 'Email' },
                      { value: 'tel', label: 'Phone' },
                      { value: 'date', label: 'Date' },
                    ]}
                    style={styles.segmentedButtons}
                  />
                  <SegmentedButtons
                    value={fieldType}
                    onValueChange={(value) => setFieldType(value as QuestionField['type'])}
                    buttons={[
                      { value: 'select', label: 'Dropdown' },
                      { value: 'radio', label: 'Radio' },
                      { value: 'file', label: 'Photo' },
                    ]}
                    style={styles.segmentedButtons}
                  />
                  <SegmentedButtons
                    value={fieldType}
                    onValueChange={(value) => setFieldType(value as QuestionField['type'])}
                    buttons={[
                      { value: 'helpful_links', label: 'Links' },
                      { value: 'dimensions_2d', label: '2D Size' },
                      { value: 'dimensions_3d', label: '3D Size' },
                    ]}
                    style={styles.segmentedButtons}
                  />
                </View>

                {(fieldType === 'select' || fieldType === 'radio') && (
                  <View style={styles.optionsSection}>
                    <Text variant="labelLarge" style={styles.optionsLabel}>
                      {fieldType === 'select' ? 'Dropdown' : 'Radio'} Options
                    </Text>
                    {selectOptions.map((option, index) => (
                      <View key={index} style={styles.optionRow}>
                        <TextInput
                          value={option}
                          onChangeText={(text) => updateSelectOption(index, text)}
                          mode="outlined"
                          placeholder={`Option ${index + 1}`}
                          style={styles.optionInput}
                          dense
                        />
                        <IconButton
                          icon="minus-circle"
                          size={20}
                          onPress={() => removeSelectOption(index)}
                          disabled={selectOptions.length <= 1}
                        />
                      </View>
                    ))}
                    <Button
                      mode="text"
                      onPress={addSelectOption}
                      icon="plus"
                      compact
                      style={styles.addOptionButton}
                    >
                      Add Option
                    </Button>
                    {errors.options && (
                      <HelperText type="error" visible={true}>
                        {errors.options}
                      </HelperText>
                    )}
                  </View>
                )}

                {(fieldType === 'number' || fieldType === 'plusminus') && (
                  <View style={styles.multiplicitySection}>
                    <View style={styles.switchRow}>
                      <Text variant="labelLarge">Triggers Item Multiplicity</Text>
                      <Switch
                        value={triggersMultiplicity}
                        onValueChange={setTriggersMultiplicity}
                      />
                    </View>
                    <HelperText type="info" visible={true}>
                      When enabled, entering a value {'>'} 1 will create multiple instances of this item
                    </HelperText>
                  </View>
                )}

                {fieldType === 'helpful_links' && (
                  <View style={styles.optionsSection}>
                    <Text variant="labelLarge" style={styles.optionsLabel}>
                      Links
                    </Text>
                    {links.map((link, index) => (
                      <View key={index} style={styles.linkRow}>
                        <TextInput
                          value={link.label}
                          onChangeText={(text) => {
                            const newLinks = [...links];
                            newLinks[index] = { ...newLinks[index], label: text };
                            setLinks(newLinks);
                          }}
                          mode="outlined"
                          placeholder="Display Label"
                          style={[styles.optionInput, { flex: 1 }]}
                          dense
                        />
                        <TextInput
                          value={link.url}
                          onChangeText={(text) => {
                            const newLinks = [...links];
                            newLinks[index] = { ...newLinks[index], url: text };
                            setLinks(newLinks);
                          }}
                          mode="outlined"
                          placeholder="URL"
                          style={[styles.optionInput, { flex: 2 }]}
                          dense
                          autoCapitalize="none"
                          keyboardType="url"
                        />
                        <IconButton
                          icon="minus-circle"
                          size={20}
                          onPress={() => {
                            if (links.length > 1) {
                              setLinks(links.filter((_, i) => i !== index));
                            }
                          }}
                          disabled={links.length <= 1}
                        />
                      </View>
                    ))}
                    <Button
                      mode="text"
                      onPress={() => setLinks([...links, { url: '', label: '' }])}
                      icon="plus"
                      compact
                      style={styles.addOptionButton}
                    >
                      Add Link
                    </Button>
                    {errors.options && (
                      <HelperText type="error" visible={true}>
                        {errors.options}
                      </HelperText>
                    )}
                  </View>
                )}

                {(fieldType === 'dimensions_2d' || fieldType === 'dimensions_3d') && (
                  <View style={styles.optionsSection}>
                    <Text variant="labelLarge" style={styles.optionsLabel}>
                      Unit of Measurement
                    </Text>
                    <SegmentedButtons
                      value={unit}
                      onValueChange={(value) => setUnit(value as 'in' | 'ft' | 'cm' | 'm')}
                      buttons={[
                        { value: 'in', label: 'Inches' },
                        { value: 'ft', label: 'Feet' },
                        { value: 'cm', label: 'cm' },
                        { value: 'm', label: 'Meters' },
                      ]}
                      style={styles.segmentedButtons}
                    />
                  </View>
                )}
              </View>
            </ScrollView>

            <Divider />

            <View style={styles.actions}>
              <Button
                mode="text"
                onPress={onDismiss}
                disabled={loading}
                style={styles.actionButton}
              >
                Cancel
              </Button>
              <Button
                mode="contained"
                onPress={handleSave}
                loading={loading}
                disabled={loading}
                style={styles.actionButton}
              >
                {existingField ? 'Update' : 'Add'} Question
              </Button>
            </View>
          </Surface>
        </KeyboardAvoidingView>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modalContent: {
    backgroundColor: theme.colors.surface || 'white',
    margin: 20,
    marginTop: 80,
    marginBottom: 80,
    borderRadius: 12,
    height: '75%',
    display: 'flex',
    flexDirection: 'column',
  },
  keyboardAvoid: {
    flex: 1,
  },
  surface: {
    borderRadius: 8,
    backgroundColor: theme.colors.surface,
    maxHeight: '100%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  title: {
    flex: 1,
  },
  closeButton: {
    margin: 0,
  },
  content: {
    flexGrow: 0,
    flexShrink: 1,
  },
  form: {
    padding: spacing.md,
  },
  input: {
    marginBottom: spacing.xs,
  },
  typeSection: {
    marginTop: spacing.md,
    marginBottom: spacing.md,
  },
  typeLabel: {
    marginBottom: spacing.sm,
  },
  segmentedButtons: {
    marginBottom: spacing.xs,
  },
  optionsSection: {
    marginTop: spacing.md,
    marginBottom: spacing.md,
  },
  optionsLabel: {
    marginBottom: spacing.sm,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs,
    gap: spacing.xs,
  },
  optionInput: {
    flex: 1,
    marginRight: spacing.xs,
  },
  addOptionButton: {
    alignSelf: 'flex-start',
    marginTop: spacing.xs,
  },
  multiplicitySection: {
    marginTop: spacing.md,
    marginBottom: spacing.md,
  },
  switchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: spacing.md,
    gap: spacing.sm,
  },
  actionButton: {
    minWidth: 100,
  },
});
